import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FeedbackModal } from '../components/FeedbackModal';
import type { OpenFeedbackModalPayload } from '../types/feedback';

export type { OpenFeedbackModalPayload };

type FeedbackModalContextValue = {
  openFeedback: (payload: OpenFeedbackModalPayload) => void;
  closeFeedback: () => void;
};

const FeedbackModalContext = createContext<FeedbackModalContextValue | null>(null);

export function FeedbackModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const [source, setSource] = useState('general');
  const [metadata, setMetadata] = useState<Record<string, unknown> | undefined>();

  const openFeedback = useCallback((payload: OpenFeedbackModalPayload) => {
    setSource(payload.source);
    setMetadata(payload.metadata);
    setOpen(true);
  }, []);

  const closeFeedback = useCallback(() => {
    setOpen(false);
  }, []);

  const value = useMemo(() => ({ openFeedback, closeFeedback }), [openFeedback, closeFeedback]);

  return (
    <FeedbackModalContext.Provider value={value}>
      {children}
      <FeedbackModal isOpen={isOpen} onClose={closeFeedback} source={source} metadata={metadata} />
    </FeedbackModalContext.Provider>
  );
}

export function useFeedbackModal(): FeedbackModalContextValue {
  const ctx = useContext(FeedbackModalContext);
  if (!ctx) {
    throw new Error('useFeedbackModal must be used within FeedbackModalProvider');
  }
  return ctx;
}

export function useFeedbackModalOptional(): FeedbackModalContextValue | null {
  return useContext(FeedbackModalContext);
}
